/**
 * Cálculo de horários livres (plano, seção 17.1).
 *
 * 1. Monta as faixas abertas de cada data (modo fixo: regras do dia da semana;
 *    modo semanal: janelas da data; domicílio: janelas de domicílio).
 * 2. Subtrai atendimentos ativos e bloqueios, cada um acrescido do intervalo
 *    (e do deslocamento, quando um dos dois lados é em domicílio).
 * 3. Gera inícios possíveis em passos fixos (alinhados ao relógio) em que o serviço cabe inteiro.
 * 4. Remove inícios antes de agora + antecedência mínima e depois de hoje + dias à frente.
 * 5. Devolve a lista agrupada por dia, no fuso da profissional.
 *
 * A mesma regra existe em PHP na API (api/app/Domain/Agenda/Availability.php).
 * Os dois lados rodam os mesmos casos de teste de fixtures/availability.json.
 */
import type { AppointmentStatus, Location } from '../schemas/entities';
import {
  type LocalDate,
  type LocalTime,
  DAY,
  MINUTE,
  addDays,
  dateRange,
  diffDays,
  parseTime,
  todayIn,
  toIso,
  toMs,
  tzOffsetMinutes,
  utcToZoned,
  weekdayOf,
  zonedToUtc,
} from './time';

/** Status que ocupam a agenda. */
export const BUSY_STATUSES: readonly AppointmentStatus[] = ['pending', 'scheduled', 'confirmed', 'completed'];

export function isBusyStatus(status: AppointmentStatus): boolean {
  return BUSY_STATUSES.includes(status);
}

export interface AvailabilitySettings {
  timezone: string;
  schedule_mode: 'fixed' | 'weekly';
  buffer_min: number;
  slot_step_min: number;
  min_notice_min: number;
  max_days_ahead: number;
  travel_min: number;
}

export interface RuleLike {
  weekday: number;
  start: LocalTime;
  end: LocalTime;
  deleted_at?: string | null;
}

export interface WindowLike {
  date: LocalDate;
  start: LocalTime;
  end: LocalTime;
  kind: 'range' | 'home_window';
  deleted_at?: string | null;
}

export interface BusyLike {
  id?: string;
  start_utc: string;
  end_utc: string;
  /** só para atendimentos */
  status?: AppointmentStatus;
  location?: Location;
  deleted_at?: string | null;
}

export interface AvailabilityInput {
  settings: AvailabilitySettings;
  rules: RuleLike[];
  windows: WindowLike[];
  appointments: BusyLike[];
  blocks: BusyLike[];
  durationMin: number;
  location: Location;
  from: LocalDate;
  to: LocalDate;
  /** instante atual (UTC ms ou ISO) */
  now: number | string;
  /** ignora este atendimento (ao remarcar) */
  ignoreAppointmentId?: string;
}

export interface Slot {
  /** início em UTC (ISO) */
  start: string;
  /** hora local HH:MM */
  time: LocalTime;
}

export interface DaySlots {
  date: LocalDate;
  slots: Slot[];
}

export interface Interval {
  start: number;
  end: number;
}

/** Faixas abertas (UTC ms) de uma data, para um tipo de local. */
export function openRangesFor(
  date: LocalDate,
  location: Location,
  settings: Pick<AvailabilitySettings, 'timezone' | 'schedule_mode'>,
  rules: RuleLike[],
  windows: WindowLike[],
): Array<Interval & { kind: 'range' | 'home_window' }> {
  const tz = settings.timezone;
  const liveWindows = windows.filter((w) => !w.deleted_at && w.date === date);
  const homeWindows = liveWindows.filter((w) => w.kind === 'home_window');

  const toInterval = (start: LocalTime, end: LocalTime, kind: 'range' | 'home_window') => {
    const s = zonedToUtc(date, start, tz);
    const e = zonedToUtc(date, end, tz);
    return { start: s, end: e, kind };
  };

  let regular: Array<Interval & { kind: 'range' | 'home_window' }>;
  if (settings.schedule_mode === 'fixed') {
    const wd = weekdayOf(date);
    regular = rules
      .filter((r) => !r.deleted_at && r.weekday === wd)
      .map((r) => toInterval(r.start, r.end, 'range'));
  } else {
    regular = liveWindows.filter((w) => w.kind === 'range').map((w) => toInterval(w.start, w.end, 'range'));
  }

  if (location === 'home') {
    // Dia com janela de domicílio: o cliente de domicílio só entra na janela.
    // Dia sem janela: vale o horário normal (com deslocamento nas pontas).
    if (homeWindows.length > 0) {
      return normalize(homeWindows.map((w) => toInterval(w.start, w.end, 'home_window')));
    }
    return normalize(regular);
  }

  // Atendimento no espaço: durante uma janela de domicílio ela está fora.
  const away = homeWindows.map((w) => toInterval(w.start, w.end, 'home_window'));
  return normalize(subtractAll(regular, away).map((i) => ({ ...i, kind: 'range' as const })));
}

/** Horários livres para a página do cliente. */
export function computeSlots(input: AvailabilityInput): DaySlots[] {
  const { settings, durationMin, location } = input;
  const tz = settings.timezone;
  const now = toMs(input.now);
  const step = Math.max(5, settings.slot_step_min);
  const duration = durationMin * MINUTE;
  const isHome = location === 'home';
  const travel = (isHome ? settings.travel_min : 0) * MINUTE;

  const today = todayIn(tz, now);
  const lastDate = addDays(today, settings.max_days_ahead);
  const earliest = now + settings.min_notice_min * MINUTE;

  const from = input.from < today ? today : input.from;
  const to = input.to > lastDate ? lastDate : input.to;
  if (diffDays(from, to) < 0) return [];

  const busy = busyIntervalsFor(input, isHome);
  const result: DaySlots[] = [];

  for (const date of dateRange(from, to)) {
    const ranges = openRangesFor(date, location, settings, input.rules, input.windows);
    const slots: Slot[] = [];
    for (const range of ranges) {
      // Em domicílio fora de janela, ela sai do espaço e volta: deslocamento nas pontas.
      const edge = isHome && range.kind === 'range' ? travel : 0;
      const usable: Interval = { start: range.start + edge, end: range.end - edge };
      if (usable.end <= usable.start) continue;
      for (const free of subtractAll([usable], busy)) {
        for (const start of stepStarts(free.start, free.end - duration, step, tz)) {
          if (start < earliest) continue;
          slots.push({ start: toIso(start), time: utcToZoned(start, tz).time });
        }
      }
    }
    slots.sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));
    const unique = slots.filter((s, i) => i === 0 || s.start !== slots[i - 1]!.start);
    if (unique.length > 0) result.push({ date, slots: unique });
  }
  return result;
}

/** Confere se um início específico está entre os horários livres. */
export function isSlotAvailable(input: Omit<AvailabilityInput, 'from' | 'to'>, start: string): boolean {
  const tz = input.settings.timezone;
  const date = utcToZoned(start, tz).date;
  const days = computeSlots({ ...input, from: date, to: date });
  const target = toMs(start);
  return days.some((d) => d.slots.some((s) => toMs(s.start) === target));
}

/**
 * Intervalos ocupados já expandidos pelo intervalo entre atendimentos e,
 * se um dos lados for em domicílio, pelo deslocamento.
 */
function busyIntervalsFor(input: AvailabilityInput, candidateIsHome: boolean): Interval[] {
  const { settings } = input;
  const buffer = settings.buffer_min * MINUTE;
  const travel = settings.travel_min * MINUTE;
  const out: Interval[] = [];
  for (const a of input.appointments) {
    if (a.deleted_at) continue;
    if (a.status && !isBusyStatus(a.status)) continue;
    if (input.ignoreAppointmentId && a.id === input.ignoreAppointmentId) continue;
    const gap = buffer + (candidateIsHome || a.location === 'home' ? travel : 0);
    out.push({ start: toMs(a.start_utc) - gap, end: toMs(a.end_utc) + gap });
  }
  for (const b of input.blocks) {
    if (b.deleted_at) continue;
    const gap = buffer + (candidateIsHome ? travel : 0);
    out.push({ start: toMs(b.start_utc) - gap, end: toMs(b.end_utc) + gap });
  }
  return out;
}

/** Inícios alinhados ao relógio local (múltiplos do passo desde a meia-noite). */
function stepStarts(from: number, lastStart: number, stepMin: number, tz: string): number[] {
  if (lastStart < from) return [];
  const out: number[] = [];
  const stepMs = stepMin * MINUTE;
  const localMs = from + tzOffsetMinutes(from, tz) * MINUTE;
  const sinceMidnight = localMs - Math.floor(localMs / DAY) * DAY;
  const aligned = Math.ceil(sinceMidnight / stepMs) * stepMs;
  for (let t = from + (aligned - sinceMidnight); t <= lastStart; t += stepMs) out.push(t);
  return out;
}

/** Junta intervalos sobrepostos e ordena. */
export function normalize<T extends Interval>(list: T[]): T[] {
  const sorted = list.filter((i) => i.end > i.start).sort((a, b) => a.start - b.start);
  const out: T[] = [];
  for (const cur of sorted) {
    const last = out[out.length - 1];
    if (last && cur.start <= last.end) {
      if (cur.end > last.end) out[out.length - 1] = { ...last, end: cur.end };
    } else {
      out.push({ ...cur });
    }
  }
  return out;
}

/** Subtrai `remove` de `base`. */
export function subtractAll(base: Interval[], remove: Interval[]): Interval[] {
  let current = normalize(base.map((b) => ({ start: b.start, end: b.end })));
  for (const r of normalize(remove.map((x) => ({ start: x.start, end: x.end })))) {
    const next: Interval[] = [];
    for (const c of current) {
      if (r.end <= c.start || r.start >= c.end) {
        next.push(c);
        continue;
      }
      if (r.start > c.start) next.push({ start: c.start, end: r.start });
      if (r.end < c.end) next.push({ start: r.end, end: c.end });
    }
    current = next;
  }
  return current;
}

/** Faixas abertas de um dia para a tela da profissional (sem antecedência nem limite). */
export function openRangesForDay(
  date: LocalDate,
  settings: Pick<AvailabilitySettings, 'timezone' | 'schedule_mode'>,
  rules: RuleLike[],
  windows: WindowLike[],
): Array<{ start: LocalTime; end: LocalTime; kind: 'range' | 'home_window' }> {
  const tz = settings.timezone;
  const studio = openRangesFor(date, 'studio', settings, rules, windows);
  const home = windows
    .filter((w) => !w.deleted_at && w.date === date && w.kind === 'home_window')
    .map((w) => ({ start: zonedToUtc(date, w.start, tz), end: zonedToUtc(date, w.end, tz), kind: 'home_window' as const }));
  return [...studio, ...home]
    .sort((a, b) => a.start - b.start)
    .map((i) => ({
      start: utcToZoned(i.start, tz).time,
      end: i.end >= zonedToUtc(addDays(date, 1), '00:00', tz) ? '24:00' : utcToZoned(i.end, tz).time,
      kind: i.kind,
    }));
}

/** Converte "HH:MM" em minutos e valida início < fim. */
export function isValidRange(start: LocalTime, end: LocalTime): boolean {
  try {
    return parseTime(start) < parseTime(end);
  } catch {
    return false;
  }
}
